// Real Sentry events from browser storage
export const getSentryEvents = async (filters = {}) => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 300));
  
  // Get real events from localStorage first, then fallback to window
  let realEvents = [];
  try {
    const storedEvents = localStorage.getItem('sentryEventStore');
    if (storedEvents) {
      realEvents = JSON.parse(storedEvents);
      console.log('Loaded events from localStorage:', realEvents.length);
    } else {
      realEvents = window.sentryEventStore || [];
      console.log('Loaded events from window:', realEvents.length);
    }
  } catch (e) {
    console.error('Error loading events:', e);
    realEvents = window.sentryEventStore || [];
  }
  
  // Add demo data if no real events exist
  const demoEvents = realEvents.length === 0 ? [
    {
      id: 'demo-1',
      title: 'Sentry Integration Active',
      message: 'Your app already has Sentry integrated with 25+ error capture points. Trigger real errors by using the app to see them appear here.',
      category: 'custom_message',
      level: 'info',
      timestamp: new Date(Date.now() - 1000 * 60 * 5).toISOString(),
      user: {
        id: 'system',
        email: 'system@leebai.com',
        username: 'system'
      },
      context: {
        existing_integration: true,
        capture_points: '25+ locations in codebase',
        instructions: 'Trigger real errors by using the app to see them appear here'
      },
      count: 1,
      firstSeen: new Date(Date.now() - 1000 * 60 * 5).toISOString(),
      lastSeen: new Date(Date.now() - 1000 * 60 * 5).toISOString()
    }
  ] : [];
  
  // Combine real and demo events
  let allEvents = [...realEvents, ...demoEvents];
  
  // Apply category filter
  if (filters.category && filters.category !== 'all') {
    allEvents = allEvents.filter(event => event.category === filters.category);
  }
  
  // Apply level filter
  if (filters.level && filters.level !== 'all') {
    allEvents = allEvents.filter(event => event.level === filters.level);
  }
  
  // Apply search filter
  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    allEvents = allEvents.filter(event => 
      event.title.toLowerCase().includes(searchLower) ||
      event.message.toLowerCase().includes(searchLower) ||
      event.user.email.toLowerCase().includes(searchLower)
    );
  }
  
  // Sort by timestamp (newest first)
  allEvents.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  
  // Calculate categories from all events (not filtered)
  const allEventsForStats = [...realEvents, ...demoEvents];
  const categories = {
    manual_error: allEventsForStats.filter(e => e.category === 'manual_error').length,
    unhandled_error: allEventsForStats.filter(e => e.category === 'unhandled_error').length,
    custom_message: allEventsForStats.filter(e => e.category === 'custom_message').length,
    api_error: allEventsForStats.filter(e => e.category === 'api_error').length
  };
  
  return {
    data: allEvents,
    total: allEvents.length,
    categories: categories,
    realEventsCount: realEvents.length,
    isDemo: realEvents.length === 0
  };
};

export const getSentryEventById = async (eventId) => {
  await new Promise(resolve => setTimeout(resolve, 300));
  
  const allEvents = window.sentryEventStore || [];
  const event = allEvents.find(e => e.id === eventId);
  
  if (!event) {
    throw new Error('Event not found');
  }
  
  return { data: event };
};

export const clearSentryEvents = async () => {
  await new Promise(resolve => setTimeout(resolve, 300));
  
  // Clear both window and localStorage
  window.sentryEventStore = [];
  try {
    localStorage.removeItem('sentryEventStore');
    console.log('Cleared localStorage sentryEventStore');
  } catch (e) {
    console.error('Error clearing localStorage:', e);
  }
  
  window.dispatchEvent(new CustomEvent('sentryEventsCleared'));
  
  return { success: true, message: 'All events cleared' };
};

// Utility functions
export const getCategoryIcon = (category) => {
  switch (category) {
    case 'manual_error':
      return '🔧';
    case 'unhandled_error':
      return '💥';
    case 'custom_message':
      return '📝';
    case 'api_error':
      return '🌐';
    default:
      return '❓';
  }
};

export const getCategoryColor = (category) => {
  switch (category) {
    case 'manual_error':
      return 'blue';
    case 'unhandled_error':
      return 'red';
    case 'custom_message':
      return 'green';
    case 'api_error':
      return 'orange';
    default:
      return 'gray';
  }
};

export const getLevelColor = (level) => {
  switch (level) {
    case 'error':
      return 'red';
    case 'warning':
      return 'yellow';
    case 'info':
      return 'blue';
    case 'debug':
      return 'gray';
    default:
      return 'gray';
  }
};